import React from 'react';
import { Search, RefreshCw, LogOut, WifiOff, RotateCcw, RotateCw } from 'lucide-react';
import { TabType } from '../lib/types';
import { triggerHaptic } from '../lib/haptics';

interface HeaderProps {
  activeTab: TabType;
  isOnline: boolean;
  syncStatus: 'cloud' | 'local';
  isSyncing: boolean;
  onOpenCalendar?: () => void;
  onToggleFilter?: () => void;
  onOpenSearch?: () => void;
  onReanalyze?: () => void;
  onLogout?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  activeTab,
  isOnline,
  syncStatus,
  isSyncing,
  onOpenCalendar,
  onToggleFilter,
  onOpenSearch,
  onReanalyze,
  onLogout,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}) => {
  const getTabTitle = () => {
    switch (activeTab) {
      case 'timeline':
        return 'TIMELINE';
      case 'actions':
        return 'ACTIONS';
      case 'insights':
        return 'INSIGHTS';
      case 'settings':
        return 'SETTINGS';
      default:
        return 'TIMELINE';
    }
  };

  return (
    <header
      className="sticky top-0 z-40 w-full bg-[#F8F9FA]/90 dark:bg-[#090A0F]/90 backdrop-blur-xl border-b border-slate-200/60 dark:border-zinc-800/80 transition-colors"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 48px) + 24px)', paddingBottom: '14px' }}
    >
      <div className="max-w-md mx-auto px-5 flex items-center justify-between">
        {/* Top-Left: Large Tab Title */}
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-950 dark:text-white uppercase">
            {getTabTitle()}
          </h1>
        </div>

        {/* Top-Right: Page-Specific Equally Spaced Utility Icons */}
        <div className="flex items-center space-x-2">
          {activeTab === 'timeline' && (
            <>
              {onUndo && (
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    onUndo();
                  }}
                  disabled={!canUndo}
                  className={`w-8 h-8 rounded-full bg-white dark:bg-zinc-800 border border-slate-200/70 dark:border-zinc-700/80 flex items-center justify-center text-slate-700 dark:text-zinc-200 shadow-xs active:scale-95 transition-all ${
                    !canUndo ? 'opacity-35 cursor-not-allowed' : ''
                  }`}
                  title="Undo last change"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              {onRedo && (
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    onRedo();
                  }}
                  disabled={!canRedo}
                  className={`w-8 h-8 rounded-full bg-white dark:bg-zinc-800 border border-slate-200/70 dark:border-zinc-700/80 flex items-center justify-center text-slate-700 dark:text-zinc-200 shadow-xs active:scale-95 transition-all ${
                    !canRedo ? 'opacity-35 cursor-not-allowed' : ''
                  }`}
                  title="Redo change"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                </button>
              )}
              {onOpenSearch && (
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    onOpenSearch();
                  }}
                  className="w-8 h-8 rounded-full bg-white dark:bg-zinc-800 border border-slate-200/70 dark:border-zinc-700/80 flex items-center justify-center text-slate-700 dark:text-zinc-200 shadow-xs active:scale-95 transition-all"
                  title="Search Meals & Symptoms"
                >
                  <Search className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}

          {activeTab === 'insights' && onReanalyze && (
            <button
              type="button"
              onClick={onReanalyze}
              disabled={isSyncing}
              className="w-8 h-8 rounded-full bg-white dark:bg-zinc-800 border border-slate-200/70 dark:border-zinc-700/80 flex items-center justify-center text-slate-700 dark:text-zinc-200 shadow-xs active:scale-95 transition-all disabled:opacity-50"
              title="Refresh AI Analysis"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
            </button>
          )}

          {activeTab === 'settings' && onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="w-8 h-8 rounded-full bg-white dark:bg-zinc-800 border border-slate-200/70 dark:border-zinc-700/80 flex items-center justify-center text-rose-500 shadow-xs active:scale-95 transition-all"
              title="Log Out"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Minimal Offline indicator if offline */}
          {!isOnline && (
            <div className="p-1.5 rounded-full text-rose-500 bg-rose-50 dark:bg-rose-950/40" title="Offline">
              <WifiOff className="w-3.5 h-3.5" />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
